/**
 * useLeads — Iter 156.10. Extracted from `LeadsPage.jsx`.
 * Owns fetch state, filters, pagination, WATI sync, create + export handlers.
 */
import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { leadsApi, watiApi, exportApi, usersApi } from "../../lib/api";

export default function useLeads(user) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [salespeople, setSalespeople] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [assignedFilter, setAssignedFilter] = useState("");
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [showFilters, setShowFilters] = useState(false);

  const [showNewLead, setShowNewLead] = useState(false);
  const [newLeadData, setNewLeadData] = useState({ name: "", phone: "", message: "" });

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      if (categoryFilter) params.category = categoryFilter;
      if (assignedFilter) params.assigned_to = assignedFilter;
      if (dateRange?.from) params.date_from = format(dateRange.from, "yyyy-MM-dd");
      if (dateRange?.to) params.date_to = format(dateRange.to, "yyyy-MM-dd");
      const response = await leadsApi.getAll(params);
      setLeads(response.data.leads);
      setTotal(response.data.total);
      setPages(response.data.pages);
    } catch {
      toast.error("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, categoryFilter, assignedFilter, dateRange]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "super_admin" || user?.role === "leader") {
      usersApi.getAll().then((res) => {
        setSalespeople(res.data.users.filter((u) => u.role === "salesperson" && u.is_active));
      }).catch((e) => console.debug("leads: salespeople fetch failed", e));
    }
  }, [user]);

  const handleSyncWati = async () => {
    setSyncing(true);
    try {
      await watiApi.sync();
      await watiApi.syncMessages();
      toast.info("Sync started - contacts imported, messages syncing in background");
      const poll = setInterval(async () => {
        try {
          const res = await watiApi.getSyncStatus();
          if (res.data.status !== "running") {
            clearInterval(poll);
            setSyncing(false);
            if (res.data.status === "completed") toast.success(`Synced ${res.data.messages_synced} messages`);
            fetchLeads();
          }
        } catch { clearInterval(poll); setSyncing(false); }
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to sync");
      setSyncing(false);
    }
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();
    try {
      await leadsApi.create(newLeadData);
      toast.success("Lead created successfully");
      setShowNewLead(false);
      setNewLeadData({ name: "", phone: "", message: "" });
      fetchLeads();
    } catch {
      toast.error("Failed to create lead");
    }
  };

  const handleExport = async () => {
    try {
      const response = await exportApi.leads();
      const data = response.data.leads;
      const headers = ["Name", "Phone", "Status", "Category", "Source", "Created At"];
      const rows = data.map((l) => [l.name, l.phone, l.status, l.category || "", l.source, l.created_at]);
      const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `leads_export_${new Date().toISOString().split("T")[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Leads exported successfully");
    } catch {
      toast.error("Failed to export leads");
    }
  };

  const clearFilters = () => {
    setSearch(""); setStatusFilter(""); setCategoryFilter("");
    setAssignedFilter(""); setDateRange({ from: null, to: null }); setPage(1);
  };

  const hasFilters = !!(search || statusFilter || categoryFilter || assignedFilter || dateRange?.from);

  return {
    leads, loading, syncing, total, page, setPage, pages, salespeople,
    search, setSearch, statusFilter, setStatusFilter, categoryFilter, setCategoryFilter,
    assignedFilter, setAssignedFilter, dateRange, setDateRange,
    showFilters, setShowFilters,
    showNewLead, setShowNewLead, newLeadData, setNewLeadData,
    handleSyncWati, handleCreateLead, handleExport,
    clearFilters, hasFilters,
  };
}
